import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ConfirmDialog from "@/components/ConfirmDialog";
import { MANAGE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function Rooms() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user.role);
  const [rooms, setRooms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", active: true });
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    const { data } = await api.get("/rooms");
    setRooms(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openDialog = (room) => {
    setEditing(room || null);
    setForm(room ? { name: room.name, active: room.active } : { name: "", active: true });
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) await api.put(`/rooms/${editing.id}`, form);
      else await api.post("/rooms", form);
      toast.success("Sala salva.");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/rooms/${deleting.id}`);
      toast.success("Sala excluída.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="rooms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Salas</h1>
          <p className="mt-1 text-sm text-muted-foreground">Espaços físicos do instituto. O sistema impede duas aulas simultâneas na mesma sala.</p>
        </div>
        {canManage && (
          <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="room-add-button">
            <Plus size={16} className="mr-2" /> Nova sala
          </Button>
        )}
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              {canManage && <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rooms.map((r) => (
              <TableRow key={r.id} className="transition-colors hover:bg-zinc-50" data-testid={`room-row-${r.name}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>
                  <Badge variant={r.active ? "default" : "secondary"} className={r.active ? "bg-primary text-primary-foreground" : ""}>
                    {r.active ? "Ativa" : "Inativa"}
                  </Badge>
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(r)} data-testid={`room-edit-${r.name}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleting(r)} data-testid={`room-delete-${r.name}`}>
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="room-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar sala" : "Nova sala"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="room-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome da sala</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="room-name-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="room-active-switch" />
              <Label>Sala ativa</Label>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="room-save-button">
              Salvar
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir sala"
        description={`Deseja excluir "${deleting?.name}"? Salas com aulas agendadas não podem ser excluídas.`}
        onConfirm={remove}
        testId="room-delete-confirm"
      />
    </div>
  );
}
